/**
 * Analytics tracking hook
 * Automatically tracks page views and provides interaction tracking
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackInteraction, trackTimeSpent } from '../lib/siteAnalyticsService';

export const useAnalytics = (contentType = 'page', contentId = null, contentSlug = null) => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const hasTrackedView = useRef(false);
  const trackingKey = `${contentType}-${contentId || 'null'}-${contentSlug || 'null'}-${location.pathname}`;

  // Track page view on mount and route change with deduplication
  useEffect(() => {
    const lastTrackedKey = sessionStorage.getItem('last_tracked_page');
    const lastTrackedTime = sessionStorage.getItem('last_tracked_time');
    const currentTime = Date.now();
    
    // Prevent duplicate tracking within 5 seconds of the same page
    const isDuplicate = lastTrackedKey === trackingKey && 
                       lastTrackedTime && 
                       (currentTime - parseInt(lastTrackedTime)) < 5000;
    
    if (!hasTrackedView.current && !isDuplicate) {
      console.log('🎯 Tracking page view:', trackingKey);
      trackPageView(contentType, contentId, contentSlug);
      hasTrackedView.current = true;
      startTimeRef.current = currentTime;
      
      // Store tracking info to prevent duplicates
      sessionStorage.setItem('last_tracked_page', trackingKey);
      sessionStorage.setItem('last_tracked_time', currentTime.toString());
    } else if (isDuplicate) {
      console.log('🚫 Skipping duplicate page view:', trackingKey);
    }
  }, [contentType, contentId, contentSlug, trackingKey]);

  // Track time spent when component unmounts or route changes
  useEffect(() => {
    return () => {
      if (hasTrackedView.current) {
        const timeSpent = Date.now() - startTimeRef.current;
        if (timeSpent > 1000) { // Only track if spent more than 1 second
          trackTimeSpent(contentType, contentId, timeSpent);
        }
      }
    };
  }, [contentType, contentId]);

  // Reset tracking on route change (but not on refresh)
  useEffect(() => {
    const currentTrackingKey = `${contentType}-${contentId || 'null'}-${contentSlug || 'null'}-${location.pathname}`;
    const lastTrackedKey = sessionStorage.getItem('last_tracked_page');
    
    // Only reset if we're actually on a different page
    if (lastTrackedKey !== currentTrackingKey) {
      hasTrackedView.current = false;
      startTimeRef.current = Date.now();
    }
  }, [location.pathname, contentType, contentId, contentSlug]);

  // Return tracking functions for manual use
  return {
    trackInteraction: (interactionType, metadata = {}) => {
      return trackInteraction(contentType, contentId, interactionType, metadata);
    },
    trackCustomEvent: (eventType, metadata = {}) => {
      return trackInteraction(contentType, contentId, eventType, metadata);
    }
  };
};

/**
 * Hook for tracking scroll depth
 */
export const useScrollTracking = (contentType, contentId) => {
  const maxScrollRef = useRef(0);
  const hasTracked25 = useRef(false);
  const hasTracked50 = useRef(false);
  const hasTracked75 = useRef(false);
  const hasTracked100 = useRef(false);
  const scrollTrackingKey = `${contentType}-${contentId || 'null'}-${window.location.pathname}`;

  useEffect(() => {
    // Reset scroll tracking for new pages
    const lastScrollPage = sessionStorage.getItem('last_scroll_page');
    if (lastScrollPage !== scrollTrackingKey) {
      hasTracked25.current = false;
      hasTracked50.current = false;
      hasTracked75.current = false;
      hasTracked100.current = false;
      maxScrollRef.current = 0;
      sessionStorage.setItem('last_scroll_page', scrollTrackingKey);
    }

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      maxScrollRef.current = Math.max(maxScrollRef.current, scrollPercent);

      // Track milestone scroll depths (only once per page)
      if (scrollPercent >= 25 && !hasTracked25.current) {
        hasTracked25.current = true;
        trackInteraction(contentType, contentId, 'scroll_25', { scrollDepth: 25 });
      }
      if (scrollPercent >= 50 && !hasTracked50.current) {
        hasTracked50.current = true;
        trackInteraction(contentType, contentId, 'scroll_50', { scrollDepth: 50 });
      }
      if (scrollPercent >= 75 && !hasTracked75.current) {
        hasTracked75.current = true;
        trackInteraction(contentType, contentId, 'scroll_75', { scrollDepth: 75 });
      }
      if (scrollPercent >= 100 && !hasTracked100.current) {
        hasTracked100.current = true;
        trackInteraction(contentType, contentId, 'scroll_100', { scrollDepth: 100 });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      
      // Track final scroll depth on unmount (only if we haven't tracked it recently)
      if (maxScrollRef.current > 0) {
        const finalScrollKey = `final_scroll_${scrollTrackingKey}`;
        const lastFinalScroll = sessionStorage.getItem(finalScrollKey);
        const currentTime = Date.now();
        
        if (!lastFinalScroll || (currentTime - parseInt(lastFinalScroll)) > 5000) {
          trackInteraction(contentType, contentId, 'scroll_final', { 
            scrollDepth: maxScrollRef.current 
          });
          sessionStorage.setItem(finalScrollKey, currentTime.toString());
        }
      }
    };
  }, [contentType, contentId, scrollTrackingKey]);

  return maxScrollRef.current;
};

/**
 * Hook for tracking click events
 */
export const useClickTracking = (contentType, contentId) => {
  const trackClick = (elementType, elementId = null, metadata = {}) => {
    return trackInteraction(contentType, contentId, 'click', {
      elementType,
      elementId,
      ...metadata
    });
  };

  return { trackClick };
};